import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { DefaultUserMenu } from '@rio-cloud/rio-user-menu-component';
import { type ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { config } from '../../config';
import { DEFAULT_LOCALE } from '../../configuration/lang/lang';
import { getDisplayMessages, getLocale } from '../../configuration/lang/langSlice';
import { isUserSessionExpired } from '../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { getIdToken } from '../../configuration/tokenHandling/tokenSlice';
import { AccountsPage } from '../components/tabs/accounts/AccountsPage';
import { AccountsSidebar } from '../components/tabs/accounts/sidebar/AccountsSidebar';
import { ClientMetricsPage } from '../components/tabs/client-metrics/ClientMetricsPage';
import { InboundConnectionsIframe } from '../components/tabs/easy-onboarding-connections/EasyOnboardingIframe';
import { PartnerIntegrationPage } from '../components/tabs/partner-integrations/PartnerIntegrationPage';
import { FeatureFlagProvider } from '../services/FeatureFlagProvider';
import { useGetAllClientsByGrantTypeQuery } from '../services/clientRegistryApi';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from './appSlice';
import IframeResizer from 'iframe-resizer-react';
import {
    ACCOUNTS_ROUTE,
    CLIENT_METRICS_ROUTE,
    DEFAULT_ROUTE,
    INBOUND_CONNECTIONS_ROUTE,
    PARTNER_INTEGRATIONS_ROUTE,
} from './routes/routes';

type AppProperties = { logout: () => void };

const getMenuOrigin = (menuUrl: string | undefined) => {
    if (!menuUrl) {
        return undefined;
    }

    try {
        return new URL(menuUrl).origin;
    } catch {
        return undefined;
    }
};

export const App = (_props: AppProperties) => {
    const displayMessages = useAppSelector(getDisplayMessages);
    const userLocale = useAppSelector(getLocale);
    const showSessionExpired = useAppSelector(
        state => isUserSessionExpired(state) && !getSessionExpiredAcknowledged(state)
    );
    const [appMenuOpen, setAppMenuOpen] = useState(false);
    const [appMenuResetKey, setAppMenuResetKey] = useState(0);
    const [navItemKey, setNavItemKey] = useState('unknown');
    const [currentNavItem, setCurrentNavItem] = useState<ReactNode>(null);

    const location = useLocation();

    const dispatch = useAppDispatch();
    const menuOrigin = getMenuOrigin(config.menuUrl);

    // initialize GET for clients here to make data accessible as soon as possible
    useGetAllClientsByGrantTypeQuery('partner_integration');
    // biome-ignore lint/style/noNonNullAssertion: suppressed out of convenience for migration to biomejs
    const idToken = useAppSelector(getIdToken)!;

    const closeAppMenu = () => {
        if (!appMenuOpen) {
            return;
        }

        document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        setAppMenuResetKey(current => current + 1);
        setAppMenuOpen(false);
    };

    useEffect(() => {
        if (location.pathname === ACCOUNTS_ROUTE) {
            setNavItemKey('accounts');
            setCurrentNavItem(<FormattedMessage id={'helpgang.sublink.accounts'} />);
        } else if (location.pathname === PARTNER_INTEGRATIONS_ROUTE) {
            setNavItemKey('partner-integrations');
            setCurrentNavItem(<FormattedMessage id={'helpgang.sublink.partner-integrations'} />);
        } else if (location.pathname === CLIENT_METRICS_ROUTE) {
            setNavItemKey('client-metrics');
            setCurrentNavItem(<FormattedMessage id={'helpgang.sublink.client-metrics'} />);
        } else if (location.pathname === INBOUND_CONNECTIONS_ROUTE) {
            setNavItemKey('inbound-connections');
            setCurrentNavItem(<FormattedMessage id={'helpgang.sublink.inbound-connections'} />);
        } else {
            setNavItemKey('unknown');
            setCurrentNavItem(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleMenuMessage = (event: MessageEvent) => {
            if (event.data?.type !== 'MENU_LINK_CLICKED') {
                return;
            }

            if (menuOrigin && event.origin !== menuOrigin) {
                return;
            }

            closeAppMenu();

            const targetWindow = event.source as WindowProxy | null;
            if (targetWindow) {
                targetWindow.postMessage({ type: 'MENU_LINK_HANDLED', requestId: event.data.requestId }, event.origin);
            }
        };

        window.addEventListener('message', handleMenuMessage);

        return () => {
            window.removeEventListener('message', handleMenuMessage);
        };
    }, [appMenuOpen, menuOrigin]);

    if (!displayMessages || !userLocale) {
        return null;
    }
    const postLogout = config.postLogoutRedirectUri
        ? {
              redirectUri: config.postLogoutRedirectUri,
              idTokenHint: idToken,
          }
        : undefined;

    const userMenu = (
        <DefaultUserMenu environment={import.meta.env.MODE} disableCookieModal={true} postLogout={postLogout} />
    );

    const appTitle = <FormattedMessage id={'helpgang.moduleName'} />;
    const appNavigator = config.menuUrl ? (
        <IframeResizer
            title={'Admin Menu Web'}
            className={'iFrameResizer'}
            src={config.menuUrl}
            sizeWidth={false}
            sizeHeight={false}
            scrolling
            style={{ width: '1px', minWidth: '100%', height: '60vh' }}
        />
    ) : undefined;

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <FeatureFlagProvider>
                <ApplicationLayout>
                    <ApplicationLayout.Header>
                        <ApplicationHeader
                            key={appMenuResetKey}
                            appNavigator={appNavigator}
                            label={appTitle}
                            navItems={[
                                {
                                    key: navItemKey,
                                    route: (
                                        <div className={'text-medium text-color-dark text-uppercase cursor-default'}>
                                            {currentNavItem}
                                        </div>
                                    ),
                                },
                            ]}
                            actionBarItems={[userMenu]}
                            onToggleAppMenu={setAppMenuOpen}
                        />
                    </ApplicationLayout.Header>
                    <ApplicationLayout.Sidebar className={'right'}>
                        <AccountsSidebar />
                    </ApplicationLayout.Sidebar>
                    <ApplicationLayout.Body>
                        <NotificationsContainer />
                        <SessionExpiredDialog
                            locale={userLocale}
                            onClose={() => dispatch(hideSessionExpiredDialog())}
                            show={showSessionExpired}
                        />
                        <Routes>
                            <Route path={DEFAULT_ROUTE} element={<AccountsPage />} />
                            <Route path={PARTNER_INTEGRATIONS_ROUTE} element={<PartnerIntegrationPage />} />
                            <Route path={CLIENT_METRICS_ROUTE} element={<ClientMetricsPage />} />
                            <Route path={INBOUND_CONNECTIONS_ROUTE} element={<InboundConnectionsIframe />} />
                            <Route path={'/'} element={<Navigate replace={true} to='/accounts' />} />
                        </Routes>
                    </ApplicationLayout.Body>
                </ApplicationLayout>
            </FeatureFlagProvider>
        </IntlProvider>
    );
};
